import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { formatDate } from "@/lib/orderUtils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Pencil, Power, PowerOff, Users, FileSpreadsheet } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CLIENT_COLORS, clientColor } from "@/lib/clientColors";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];

const FIELDS = [
  { key: "name", label: "Nome referente", cols: 2 },
  { key: "company", label: "Ragione sociale", cols: 2 },
  { key: "vat_number", label: "Partita IVA", cols: 1 },
  { key: "tax_code", label: "Codice Fiscale", cols: 1 },
  { key: "address", label: "Indirizzo", cols: 2 },
  { key: "zip_code", label: "CAP", cols: 1 },
  { key: "city", label: "Città", cols: 1 },
  { key: "province", label: "Prov.", cols: 1 },
  { key: "sdi_code", label: "Codice SDI", cols: 1 },
  { key: "pec", label: "PEC", cols: 1 },
  { key: "phone", label: "Telefono", cols: 1 },
];

export default function ClientsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(null);
  const now = new Date();
  const [repMonth, setRepMonth] = useState(String(now.getMonth() + 1));
  const [repYear, setRepYear] = useState(String(now.getFullYear()));
  const [repClient, setRepClient] = useState("all");
  const [downloading, setDownloading] = useState(null);
  const years = Array.from({ length: 5 }, (_, i) => String(now.getFullYear() - i));

  const [pwdDialog, setPwdDialog] = useState(null);
  const [reportPwd, setReportPwd] = useState("");

  const askReportPassword = (kind) => { setReportPwd(""); setPwdDialog(kind); };

  const downloadReport = async (kind = "clients", password = "") => {
    setDownloading(kind);
    const endpoint = kind === "operator" ? "/reports/operator-monthly" : "/reports/clients-monthly";
    let prefix = kind === "operator" ? "Report_collega" : "Report_clienti";
    const params = { year: Number(repYear), month: Number(repMonth) };
    if (kind === "clients" && repClient !== "all") {
      params.client_id = repClient;
      const c = clients.find((x) => x.id === repClient);
      const safe = (c?.company || c?.name || "cliente").replace(/[^\w\-]+/g, "_");
      prefix = `Report_${safe}`;
    }
    try {
      const { data } = await api.get(endpoint, {
        params,
        responseType: "blob",
        headers: { "X-Report-Password": password },
      });
      setPwdDialog(null);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${prefix}_${repYear}_${String(repMonth).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report Excel scaricato");
    } catch (e) {
      const st = e.response?.status;
      if (st === 403) toast.error("Password report non corretta");
      else if (st === 428) toast.error("Nessuna password report impostata: vai in Impostazioni → Sicurezza report");
      else toast.error("Errore nel download del report");
    } finally {
      setDownloading(null);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(isAdmin ? "/admin/clients" : "/staff/clients");
      setClients(data);
    } catch (e) {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoading(false);
    }
  };

  const setColor = async (c, color) => {
    setClients((arr) => arr.map((x) => (x.id === c.id ? { ...x, color } : x)));
    try {
      await api.patch(`/clients/${c.id}/color`, { color });
      toast.success(color ? "Colore cliente aggiornato" : "Colore rimosso");
    } catch (e) {
      toast.error("Errore nel salvare il colore");
    }
  };

  const toggleShare = async (c) => {
    const next = !c.shared_with_operator;
    setSharing(c.id);
    setClients((arr) => arr.map((x) => (x.id === c.id ? { ...x, shared_with_operator: next } : x)));
    try {
      await api.patch(`/clients/${c.id}/share`, { shared: next });
      toast.success(next ? "Cliente condiviso col collega" : "Condivisione rimossa");
    } catch (e) {
      setClients((arr) => arr.map((x) => (x.id === c.id ? { ...x, shared_with_operator: !next } : x)));
      toast.error("Errore aggiornamento condivisione");
    } finally {
      setSharing(null);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...c });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach((f) => { payload[f.key] = form[f.key] ?? ""; });
      payload.client_type = form.client_type || "laboratorio";
      await api.patch(`/admin/clients/${editing.id}`, payload);
      toast.success("Cliente aggiornato");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    const next = c.active === false;
    try {
      await api.patch(`/admin/clients/${c.id}`, { active: next });
      toast.success(next ? "Cliente attivato" : "Cliente disattivato");
      load();
    } catch (e) {
      toast.error("Errore aggiornamento stato");
    }
  };

  const filtered = useMemo(() => {
    if (!q) return clients;
    const s = q.toLowerCase();
    return clients.filter(
      (c) =>
        c.name?.toLowerCase().includes(s) ||
        c.company?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.vat_number?.toLowerCase().includes(s)
    );
  }, [clients, q]);

  return (
    <DashboardLayout title="Gestione Clienti" subtitle={isAdmin ? "Elenco anagrafiche, modifica dati di fatturazione e stato account." : "Elenco clienti e stato di condivisione dei casi."}>
      {isAdmin && (
      <Card className="mb-6 border-slate-200 bg-slate-50/60">
        <div className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex items-center gap-2 text-slate-700">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="font-semibold text-sm">Report Excel mensile</p>
              <p className="text-xs text-slate-500">Report clienti (lavorazioni per cliente e tipo) · Casi collega (ordini lavorati dall'operatore).</p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2 sm:ml-auto">
            <div className="space-y-1">
              <Label className="text-xs">Cliente</Label>
              <Select value={repClient} onValueChange={setRepClient}>
                <SelectTrigger data-testid="report-client-select" className="w-56 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i clienti (report completo)</SelectItem>
                  {[...clients]
                    .sort((a, b) => (a.company || a.name || "").localeCompare(b.company || b.name || ""))
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Mese</Label>
              <Select value={repMonth} onValueChange={setRepMonth}>
                <SelectTrigger data-testid="report-month-select" className="w-36 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Anno</Label>
              <Select value={repYear} onValueChange={setRepYear}>
                <SelectTrigger data-testid="report-year-select" className="w-24 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="download-report-button" onClick={() => askReportPassword("clients")} disabled={!!downloading} className="bg-emerald-600 hover:bg-emerald-700">
              {downloading === "clients" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />} Report clienti
            </Button>
            <Button data-testid="download-operator-report-button" onClick={() => askReportPassword("operator")} disabled={!!downloading} className="bg-amber-600 hover:bg-amber-700">
              {downloading === "operator" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />} Casi collega
            </Button>
          </div>
        </div>
      </Card>
      )}

      <Dialog open={!!pwdDialog} onOpenChange={(o) => !o && setPwdDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="report-password-dialog">
          <DialogHeader>
            <DialogTitle>Password report</DialogTitle>
            <DialogDescription>Inserisci la password di protezione per scaricare il {pwdDialog === "operator" ? "report collega" : "report clienti"}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); downloadReport(pwdDialog, reportPwd); }} className="space-y-3">
            <Input data-testid="report-password-input" type="password" autoFocus value={reportPwd} onChange={(e) => setReportPwd(e.target.value)} placeholder="Password report" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwdDialog(null)}>Annulla</Button>
              <Button type="submit" data-testid="report-password-submit" disabled={!reportPwd || !!downloading} className="bg-teal-600 hover:bg-teal-700">
                {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Scarica
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, ragione sociale, email, P.IVA..." className="pl-9" />
          </div>
          <Badge variant="secondary" className="ml-auto">{filtered.length} clienti</Badge>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-4 font-medium text-slate-700">Nessun cliente</p>
          </div>
        ) : (
          <Table data-testid="clients-table">
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                {isAdmin && <TableHead>Colore</TableHead>}
                <TableHead>Ragione sociale</TableHead>
                <TableHead>Tipo</TableHead>
                {isAdmin && <TableHead>P.IVA</TableHead>}
                <TableHead>Città</TableHead>
                <TableHead>Ordini</TableHead>
                <TableHead>Condiviso col collega</TableHead>
                {isAdmin && <TableHead>Stato</TableHead>}
                {isAdmin && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid="client-row" className="hover:bg-slate-50">
                  <TableCell style={clientColor(c.color) ? { backgroundColor: clientColor(c.color).bg, boxShadow: `inset 4px 0 0 ${clientColor(c.color).border}` } : undefined}>
                    <div className="font-medium text-slate-800">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" data-testid="client-color-trigger" title="Scegli colore cliente" className="h-7 w-7 rounded-full border-2 border-white shadow ring-1 ring-slate-300 hover:scale-110 transition-transform" style={{ backgroundColor: clientColor(c.color)?.bg || "#fff" }} />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-3" align="start">
                          <p className="text-xs font-medium text-slate-600 mb-2">Colore in griglia ordini</p>
                          <div className="grid grid-cols-5 gap-2" data-testid="client-color-palette">
                            {CLIENT_COLORS.map((col) => (
                              <button key={col.key} type="button" title={col.label} data-testid={`client-color-${col.key}`} onClick={() => setColor(c, col.key)}
                                className={`h-8 w-8 rounded-full transition-transform hover:scale-110 ${c.color === col.key ? "ring-2 ring-teal-600 ring-offset-2" : "ring-1 ring-slate-200"}`}
                                style={{ backgroundColor: col.bg, borderBottom: `3px solid ${col.border}` }} />
                            ))}
                          </div>
                          <button type="button" data-testid="client-color-none" onClick={() => setColor(c, null)} className="mt-3 text-xs text-slate-500 hover:text-red-600">Nessun colore</button>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                  )}
                  <TableCell className="text-slate-600">{c.company || "—"}</TableCell>
                  <TableCell>
                    <Badge className={(c.client_type === "studio") ? "bg-sky-100 text-sky-700 border-0 hover:bg-sky-100" : "bg-violet-100 text-violet-700 border-0 hover:bg-violet-100"}>
                      {c.client_type === "studio" ? "Studio" : "Laboratorio"}
                    </Badge>
                  </TableCell>
                  {isAdmin && <TableCell className="font-mono text-xs text-slate-600">{c.vat_number || "—"}</TableCell>}
                  <TableCell className="text-slate-600">{c.city ? `${c.city} (${c.province || "?"})` : "—"}</TableCell>
                  <TableCell className="text-slate-600">{c.order_count ?? 0}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {isAdmin ? (
                        <>
                          <Switch data-testid="client-share-toggle" checked={!!c.shared_with_operator} disabled={sharing === c.id} onCheckedChange={() => toggleShare(c)} />
                          <span className="text-xs text-slate-400">{c.shared_with_operator ? "Sì" : "No"}</span>
                        </>
                      ) : (
                        <Badge className={c.shared_with_operator ? "bg-emerald-100 text-emerald-700 border-0 hover:bg-emerald-100" : "bg-slate-100 text-slate-500 border-0 hover:bg-slate-100"}>
                          {c.shared_with_operator ? "Condiviso" : "Non condiviso"}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {c.active === false ? (
                        <Badge className="bg-rose-100 text-rose-700 border-0 hover:bg-rose-100">Disattivato</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 hover:bg-emerald-100">Attivo</Badge>
                      )}
                    </TableCell>
                  )}
                  {isAdmin && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" data-testid="edit-client-button" onClick={() => openEdit(c)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid="toggle-client-button" onClick={() => toggleActive(c)}
                        className={c.active === false ? "text-emerald-600" : "text-rose-500"}>
                        {c.active === false ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifica cliente</DialogTitle>
            <DialogDescription>{editing?.email}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-2 col-span-2">
              <Label>Categoria cliente</Label>
              <Select value={form.client_type || "laboratorio"} onValueChange={(v) => setForm({ ...form, client_type: v })}>
                <SelectTrigger data-testid="edit-client-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="laboratorio">Laboratorio</SelectItem>
                  <SelectItem value="studio">Studio dentistico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {FIELDS.map((f) => (
              <div key={f.key} className={`space-y-2 ${f.cols === 2 ? "col-span-2" : ""}`}>
                <Label>{f.label}</Label>
                <Input
                  data-testid={`edit-${f.key}`}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button data-testid="save-client-button" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
